import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { ButtonFormBrandType } from './buttonTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.button.style, {
  icon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.button.schema.icon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
  },
  button: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.button.schema.backgroundImage'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { resettable: true, transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  disabled: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.button.schema.backgroundImage'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  border: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.button.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  padding: {
    paddingTop: genBrandField({
      as: FieldNumber,
      label: t('elements.button.schema.vertical'),
      props: { width: 64 },
    }),
    paddingLeft: genBrandField({
      as: FieldNumber,
      label: t('elements.button.schema.horizontal'),
      props: { width: 64 },
    }),
  },
})

const ButtonFormStyle: ButtonFormBrandType = ({ device, subConfigs, edited, element, setForm }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)
  const isSlot = Boolean(element?.value.item?.value)
  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        {!isSlot && <FormField config={schema.icon.backgroundImage} />}
        <Typography weight='medium'>{t('elements.button.tags.active')}</Typography>
        <FormField config={schema.button.backgroundImage} />
        <FormField config={schema.button.backgroundColor} />
        <FormField config={schema.hover.backgroundColor} />

        <Typography weight='medium'>{t('elements.button.tags.inactive')}</Typography>
        <FormField config={schema.disabled.backgroundImage} />
        <FormField config={schema.disabled.backgroundColor} />

        <Typography weight='medium'>{t('elements.button.tags.border')}</Typography>
        <FormField config={schema.border.borderRadius} />
        <FormField config={schema.border.borderStyle} />
        <FormField config={schema.border.borderColor} />
        <FormField config={schema.border.borderWidth} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <Typography weight='medium'>{t('elements.button.tags.size')}</Typography>
        <FormField config={schema.padding.paddingTop} />
        <FormField config={schema.padding.paddingLeft} />

        {!isSlot && (
          <TileButton
            description={t('uiKit.button.richTextDescription')}
            icon='otherRight'
            name='showFontForm'
            onClick={handleSetTypography}
          >
            <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
            {isAnyEdited(edited.font) && <BrandTagEdited />}
          </TileButton>
        )}
      </Padding>
    </LayoutScroll>
  )
}

export default ButtonFormStyle
